// src/components/games/VocabRunGame.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppIcon from '../common/AppIcon';
import GradientIcon from '../common/GradientIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { saveGuestResult, hasClaimableGuestResult } from '../../utils/guestSession';
import GuestSignupPrompt from './GuestSignupPrompt';
import PointsEarnedRow from './PointsEarnedRow';

// 🟢 [访客模式] guest 默认 false —— 已有的 /games/vocab-run 路由行为完全不变
const VocabRunGame = ({ guest = false }) => {
  const navigate = useNavigate();
  const { user, reloadUser } = useAuth();

  // --- 状态管理 ---
  const [gameState, setGameState] = useState('menu'); // 'menu' | 'loading' | 'playing' | 'gameover'
  const [vocabularyPool, setVocabularyPool] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  
  // 游戏数值
  const [health, setHealth] = useState(100);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [feedback, setFeedback] = useState(null); // 'correct' | 'wrong'

  // 🟢 [修复] 本局真实入账的积分（后端 routes/games.js 返回的 pointsEarned）。
  // 这个游戏提交的是 score * 10，所以 50 词以上就会被 MAX_GAME_POINTS=500 截断，
  // 结算画面原来只显示 score * 10，一局 80 词会显示 800 而实际只进账 500。
  // null = 还在提交，'failed' = 没入账，number = 后端确认的真实值。
  const [pointsEarned, setPointsEarned] = useState(null);

  // 难度控制
  const decayRate = Math.min(0.5, 0.15 + (score * 0.01));
  const timerRef = useRef(null);

  // 🟢 [修复] decayRate 到达 0.5 上限后 (score >= 35) 生命值循环不再重建，
  // interval 闭包里的 score 会被冻结在 35，导致提交的分数封顶 350。
  // 用 ref 保存最新分数，endGame 从 ref 读取真实分数。
  const scoreRef = useRef(0);
  useEffect(() => { scoreRef.current = score; }, [score]);

  // 🟢 [修复] endGame 幂等保护：StrictMode 下更新函数会双调用，且生命值可能连续两帧 <=0，
  // 若不加锁会重复提交 saveGameScore。每次开始新局时重置。
  const endGameCalledRef = useRef(false);
  
  // 🟢 [新增] 记录已答对的单词集合 (避免重复)
  const correctlyAnsweredRef = useRef(new Set());
  
  // 🟢 [新增] 背景音乐 Ref
  const bgmRef = useRef(null);

  // --- 1. 初始化：拉取数据 & 初始化音频 ---
  useEffect(() => {
    // 初始化音频对象
    bgmRef.current = new Audio('/sounds/survival_bgm.mp3'); // 指向刚才生成的 wav
    bgmRef.current.loop = true; // 循环播放
    bgmRef.current.volume = 0.2; // 音量调低，以免盖过单词发音

    const initGame = async () => {
      try {
        // 🟢 [访客模式] 访客没有 user.level，固定用 core；并走公开缓存接口
        const level = guest ? 'core' : (user?.level || 'core');
        const fetchWords = (lvl, count) => guest
          ? apiService.getPublicRandomVocabulary(lvl, count)
          : apiService.getRandomVocabulary(lvl, count);

        const res = await fetchWords(level, 50);
         
        if (res.vocabulary && res.vocabulary.length >= 4) {
          setVocabularyPool(res.vocabulary);
        } else {
          // Retry logic... ('all' 也在公开接口的 level 白名单里)
          const retryRes = await fetchWords('all', 50);
          if (retryRes.vocabulary && retryRes.vocabulary.length >= 4) {
             setVocabularyPool(retryRes.vocabulary);
          } else {
             alert("Not enough words in database yet!");
             // 🟢 访客不能去 /games (在 PrivateRoute 后面，会被弹到 /login)
             navigate(guest ? '/try' : '/games');
          }
        }
      } catch (error) {
        console.error("Failed to load vocabulary:", error);
      }
    };

    // 🟢 [访客模式] 原来只有 if (user)，访客永远拉不到词、START 按钮永远是灰的
    if (guest || user) {
      initGame();
    }

    // 🟢 组件卸载时，清理音乐 + 打断发音
    return () => {
        if (bgmRef.current) {
            bgmRef.current.pause();
            bgmRef.current = null;
        }
        if (timerRef.current) clearInterval(timerRef.current);
        if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    };
    // 🟢 [修复] 只依赖 uid/level，而非整个 user 对象。否则 endGame→reloadUser() 换了
    // user 引用就会在结算界面重建音频、重新拉取 50 个词。
  }, [guest, user?.uid, user?.level, navigate]);

  // 🟢 [新增] 监听游戏状态，控制音乐播放
  useEffect(() => {
      const bgm = bgmRef.current;
      if (!bgm) return;

      if (gameState === 'playing') {
          // 开始游戏：播放音乐
          bgm.currentTime = 0; // 从头开始
          bgm.play().catch(e => console.log("Background music autoplay blocked:", e));
      } else {
          // 菜单或游戏结束：停止音乐
          bgm.pause();
      }
  }, [gameState]);

  // --- 2. 游戏逻辑: 生成题目 ---
  const generateQuestion = useCallback(() => {
    if (vocabularyPool.length < 4) return;

    // 🟢 [新增] 过滤已答对的词
    let availablePool = vocabularyPool.filter(w => !correctlyAnsweredRef.current.has(w.id));
    if (availablePool.length === 0) {
      // 词库全部答完，重置记录
      correctlyAnsweredRef.current = new Set();
      availablePool = vocabularyPool;
    }

    const correctIndex = Math.floor(Math.random() * availablePool.length);
    const correctWord = availablePool[correctIndex];

    const distractors = [];
    // 干扰项仍然可以从全量词库中取（但排除正确答案）
    const usedIndices = new Set([vocabularyPool.findIndex(w => w.id === correctWord.id)]);
    
    while (distractors.length < 3) {
      const idx = Math.floor(Math.random() * vocabularyPool.length);
      if (!usedIndices.has(idx)) {
        distractors.push(vocabularyPool[idx]);
        usedIndices.add(idx);
      }
    }

    const options = [correctWord, ...distractors].sort(() => Math.random() - 0.5);

    setCurrentQuestion({
      ...correctWord,
      options: options
    });

    speakWord(correctWord.word);

  }, [vocabularyPool]);

  // --- 3. 游戏逻辑: 生命值循环 ---
  // 🟢 [修复] 更新函数保持纯粹 (只夹取数值)，不在里面调用 endGame —— 否则 StrictMode
  // 双调用会重复结算。归零判定移到下面的独立 effect。
  useEffect(() => {
    if (gameState === 'playing') {
      timerRef.current = setInterval(() => {
        setHealth((prev) => Math.max(0, prev - decayRate));
      }, 50);
    }
    return () => clearInterval(timerRef.current);
  }, [gameState, decayRate]);

  // 生命值归零 → 结束 (在更新函数外触发，配合 endGame 幂等锁确保只结算一次)
  useEffect(() => {
    if (gameState === 'playing' && health <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      endGame();
    }
  }, [health, gameState]);

  const speakWord = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel(); // 🟢 播放新词前打断旧词
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.lang = 'en-GB'; // 🟢 确保英式发音
      
      // 声音稍微大一点，盖过BGM
      utterance.volume = 1.0; 
      
      speechSynthesis.speak(utterance);
    }
  };

  const startGame = () => {
    if (vocabularyPool.length === 0) {
      setGameState('loading');
      return;
    }
    setScore(0);
    setHealth(100);
    setStreak(0);
    setPointsEarned(null);   // 🟢 新的一局，清掉上一局的入账结果
    correctlyAnsweredRef.current = new Set(); // 🟢 每次开始新游戏清空记录
    endGameCalledRef.current = false;         // 🟢 解锁本局结算
    setGameState('playing');
    generateQuestion();
  };

  const handleAnswer = (selectedOption) => {
    if (gameState !== 'playing' || !currentQuestion) return;

    if (selectedOption.id === currentQuestion.id) {
      setScore(s => s + 1);
      setStreak(s => s + 1);
      const healAmount = 15 + Math.min(streak, 10);
      setHealth(h => Math.min(100, h + healAmount)); 
      setFeedback('correct');
      
      correctlyAnsweredRef.current.add(currentQuestion.id); // 🟢 记录答对的词
      
      // 🟢 答对音效 (可选，如果已有 playSound 函数可引用)
      // new Audio('/sounds/success.wav').play().catch(()=>{});

      setTimeout(() => {
        setFeedback(null);
        generateQuestion();
      }, 200); 
    } else {
      setHealth(h => Math.max(0, h - 25)); 
      setStreak(0);
      setFeedback('wrong');
      
      // 🟢 答错音效 (可选)
      // new Audio('/sounds/fail.wav').play().catch(()=>{});

      setTimeout(() => setFeedback(null), 400);
    }
  };

  const endGame = async () => {
    // 🟢 幂等：本局已结算过就直接返回，避免重复 saveGameScore
    if (endGameCalledRef.current) return;
    endGameCalledRef.current = true;

    setGameState('gameover');

    // 🟢 游戏结束：停止音乐 (虽然 useEffect 会处理，但为了即时性手动调一次)
    if(bgmRef.current) bgmRef.current.pause();

    const finalScore = scoreRef.current; // 🟢 读取真实分数，避免闭包冻结

    // 🟢 [访客模式] 不调用任何需要 Token 的接口，只把这一局留在本 tab。
    // score 用 *10 与登录态提交的口径保持一致（服务端仍会 clamp 到 500）。
    if (guest) {
      saveGuestResult({
        gameType: 'vocab-run',
        score: finalScore * 10,
        accuracy: 100,
        wordsCorrect: finalScore,
        wordsTotal: finalScore,
        timeSpent: 0,
        displayScore: finalScore * 10,
        playedAt: Date.now()
      });
      return;
    }

    if (finalScore > 0) {
      try {
        const saveRes = await apiService.saveGameScore({
          gameType: 'vocab-run',
          score: finalScore * 10,
          accuracy: 100,
          wordsCompleted: finalScore
        });

        // 🟢 [修复] 用后端真实入账的积分，而不是客户端的 score * 10
        setPointsEarned(saveRes?.pointsEarned ?? null);

        // 刷新用户信息 (更新积分显示)
        await reloadUser();

        // 🟢 [修复] 触发 'game_played' 徽章：用后端事务返回的累计场次
        // (旧逻辑从 users 文档读 totalGamesPlayed，该字段只在 userProgress 上，永远是 1)
        const totalGames = saveRes?.totalGamesPlayed || 0;
        if (totalGames > 0) await apiService.checkBadges('game_played', totalGames);

      } catch (e) {
        console.error("Failed to save score:", e);
        // 🟢 [修复] 只在还没拿到入账结果时才标记失败 —— 这个 catch 也会捕获
        // checkBadges 的报错，那时积分已经入账，不能把真实数字覆盖掉
        setPointsEarned(prev => (typeof prev === 'number' ? prev : 'failed'));
      }
    }
  };

  // --- 渲染 UI (保持之前的样式修改) ---

  if (gameState === 'loading') {
    return <div className="min-h-screen flex items-center justify-center text-2xl font-bold text-white">Loading Words...</div>;
  }

  if (gameState === 'menu') {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center p-6">
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl shadow-2xl p-10 max-w-lg w-full text-center border-4 border-blue-400"
        >
          <div className="text-6xl mb-6">⚡</div>
          <h1 className="text-4xl sm:text-5xl font-black mb-4 text-slate-800 font-kid tracking-wider">
            VOCAB SURVIVAL
          </h1>
          <p className="text-slate-600 mb-8 text-lg font-medium">
            The flood is rising! Answer quickly to stay alive.<br/>
            <span className="text-sm text-blue-500 mt-2 block">
              (Difficulty increases as you score!)
            </span>
          </p>
          <button 
            onClick={startGame} 
            disabled={vocabularyPool.length === 0}
            className="w-full px-10 py-5 bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white rounded-2xl text-2xl font-black shadow-lg transform transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {vocabularyPool.length === 0 ? 'Loading...' : 'START RUNNING'}
          </button>
          
          <button onClick={() => navigate(guest ? '/try' : '/games')} className="mt-4 text-slate-400 font-bold hover:text-slate-600">
            {guest ? 'More Games' : 'Back to Menu'}
          </button>
        </motion.div>

        {/* 🟢 [访客模式] F5 之后回到菜单页，但成绩还在 sessionStorage 里 ——
            以细横幅形态提示「随时可以领」 */}
        {guest && hasClaimableGuestResult() && (
          <GuestSignupPrompt bannerOnly onPlayAgain={startGame} />
        )}
      </div>
    );
  }

  if (gameState === 'gameover') {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center p-6">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-slate-900 text-white rounded-3xl shadow-2xl p-10 max-w-lg w-full text-center border-4 border-slate-700"
        >
          <div className="text-7xl mb-6">💀</div>
          <h2 className="text-4xl font-bold mb-2">Run Ended</h2>
          <p className="text-gray-400 mb-6 text-lg">You survived {score} words!</p>
          
          <div className="bg-slate-800 rounded-xl p-6 mb-8">
            <div className="text-sm text-gray-400 uppercase font-bold tracking-widest mb-1">Total Score</div>
            <div className="text-6xl font-black text-cyan-400">
              {score * 10}
            </div>
            {/* 🟢 访客不显示积分行 —— 由注册弹窗负责说明 */}
            {!guest && (
              <div className="mt-4 border-t border-slate-700 pt-4 [&_*]:!text-gray-300">
                <PointsEarnedRow score={score * 10} pointsEarned={pointsEarned} />
              </div>
            )}
          </div>
          
          <div className="space-y-3">
            <button onClick={startGame} className="w-full px-6 py-4 bg-green-500 hover:bg-green-600 rounded-xl font-bold text-lg transition-colors shadow-lg">Try Again</button>
            <button onClick={() => navigate(guest ? '/try' : '/games')} className="w-full px-6 py-4 bg-slate-700 hover:bg-slate-600 rounded-xl font-bold text-lg transition-colors">{guest ? 'More Games' : 'Exit to Menu'}</button>
          </div>
        </motion.div>

        {/* 🟢 [访客模式] 结算后叠加快速注册引导；登录用户完全不渲染 */}
        {guest && <GuestSignupPrompt onPlayAgain={startGame} />}
      </div>
    );
  }

  // 游戏进行中
  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center p-4">
      {/* 🟢 [Req7] 低能量时整屏边缘泛红警示 */}
      {health < 30 && (
        <div className="fixed inset-0 pointer-events-none z-[60] animate-pulse" style={{ boxShadow: 'inset 0 0 70px 18px rgba(239,68,68,0.65)' }} aria-hidden="true"></div>
      )}
<div className={`relative flex flex-col min-h-[600px] max-h-[85vh] w-full max-w-xl bg-white rounded-3xl shadow-2xl border-4 overflow-y-auto transition-all duration-300 ${health < 30 ? 'border-red-500 ring-4 ring-red-500/50 animate-pulse' : 'border-slate-200'}`}>
        
        {/* 顶部状态栏 */}
        <div className="sticky top-0 bg-white p-6 pb-4 shadow-sm z-30 border-b border-slate-100">
          <div className="flex justify-between items-end mb-3">
            <span className="text-slate-500 font-bold text-xs tracking-wider flex items-center gap-1">
              <AppIcon name="heart" className="text-red-500" /> ENERGY
            </span>
            <span className="text-4xl font-black text-slate-800">{score}</span>
          </div>
          
          {/* 生命条 */}
          <div className="relative">
            <div className="h-6 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200">
              <motion.div
                className={`h-full shadow-inner ${
                  health > 50 ? 'bg-gradient-to-r from-green-400 to-green-500' :
                  health > 20 ? 'bg-gradient-to-r from-yellow-400 to-orange-500' : 'bg-gradient-to-r from-red-500 to-red-600'
                }`}
                style={{ width: `${health}%` }}
                transition={{ duration: 0.1, ease: "linear" }}
              />
            </div>
            {/* 🟢 跟随能量条移动的奔跑小人 */}
            <motion.div
              className="absolute -top-5 z-10 pointer-events-none text-3xl leading-none drop-shadow-md"
              style={{ left: `calc(${Math.max(0, Math.min(100, health))}% - 18px)` }}
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 0.4, repeat: Infinity, ease: 'easeInOut' }}
              aria-hidden="true"
            >
              <span className="inline-block -scale-x-100">🏃</span>
            </motion.div>
          </div>
        </div>

        {/* 中间：单词区 */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center relative select-none bg-slate-50 min-h-[200px]">
          <AnimatePresence>
            {feedback && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 0.3 }} 
                exit={{ opacity: 0 }}
                className={`absolute inset-0 z-0 ${
                  feedback === 'correct' ? 'bg-green-500' : 'bg-red-500'
                }`} 
              />
            )}
          </AnimatePresence>
          
          <div className="z-10 relative">
            <span className="inline-block px-4 py-1.5 bg-slate-200 text-slate-600 rounded-full text-sm font-bold mb-6 uppercase tracking-widest">
              {currentQuestion?.partOfSpeech}
            </span>
            
            <motion.h2 
              key={currentQuestion?.word} 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-5xl md:text-6xl font-black text-slate-800 mb-8 tracking-tight drop-shadow-sm break-words max-w-full"
            >
              {currentQuestion?.word}
            </motion.h2>

            <button 
              onClick={() => speakWord(currentQuestion?.word)}
              className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 flex items-center justify-center transition-colors mx-auto"
            >
              <AppIcon name="volume-up" className="text-xl" />
            </button>
          </div>
        </div>

        {/* 底部：选项 */}
        <div className="grid grid-cols-1 gap-3 p-4 bg-white border-t border-slate-200 pb-8">
          {currentQuestion?.options.map((opt, idx) => (
            <button
              key={idx}
              onClick={() => handleAnswer(opt)}
              className="w-full p-4 text-left bg-white hover:bg-blue-50 active:bg-blue-100 border-2 border-slate-200 hover:border-blue-400 rounded-xl transition-all duration-100 group shadow-sm active:scale-[0.98] active:shadow-inner"
            >
              <div className="text-lg font-bold text-slate-700 group-hover:text-blue-700 leading-snug">
                {opt.definition}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VocabRunGame;